import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { PROJECTS_SAVE_ERROR } from './useProjectStore';
import { Project } from './types';

const useStyles = makeStyles(theme => ({
  caution: {
    color: theme.palette.error.main,
  },
}));

type ProjectDeleteDialogProps = {
  project: Project | null;
  deleteProject: (projectId: string) => Promise<void>;
  error?: string;
  onClose: () => void;
  onDeleted?: () => void;
};

export function ProjectDeleteDialog({
  project,
  deleteProject,
  error,
  onClose,
  onDeleted,
}: ProjectDeleteDialogProps) {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const [deleting, setDeleting] = useState(false);

  return (
    <Dialog
      open={Boolean(project)}
      onClose={() => {
        if (!deleting) {
          onClose();
        }
      }}
    >
      <DialogTitle>{t('deleteDialog.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {project && project.workItems.length > 0
            ? t('deleteDialog.withWorkItemsDescription')
            : t('deleteDialog.description')}
        </Typography>
        {error === PROJECTS_SAVE_ERROR && (
          <Box mt={2}>
            <Typography variant="body2" className={classes.caution}>
              {t('deleteDialog.error')}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={deleting} onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button
          color="secondary"
          variant="contained"
          disabled={deleting || !project}
          onClick={async () => {
            if (!project) {
              return;
            }
            setDeleting(true);
            try {
              await deleteProject(project.id);
              onDeleted?.();
              onClose();
            } finally {
              setDeleting(false);
            }
          }}
        >
          {deleting ? t('actions.deleting') : t('actions.confirmDelete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
